import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/api';
import DashboardHeader from '@/components/DashboardHeader';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { 
  ArrowLeft,
  CheckCircle,
  XCircle,
  Mail,
  Clock,
  Users,
  AlertCircle
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { useTranslation } from 'react-i18next';

const SendmireinHistoryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  
  const [loading, setLoading] = useState(true);
  const [send, setSend] = useState(null);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ total: 0, success: 0, errors: 0 });
  const [filter, setFilter] = useState('ALL'); // ALL | SUCCESS | ERROR
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const logsContainerRef = useRef(null);

  useEffect(() => {
    loadData();
  }, [id, token, page]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [sendData, logsData] = await Promise.all([
        api.newslettersGetSend(token, id),
        api.newslettersGetSendLogs(token, id, page, 5),
      ]);
      
      setSend(sendData);
      setLogs(logsData.logs || []);
      setStats({
        total: logsData.total || 0,
        success: logsData.success || 0,
        errors: logsData.errors || 0,
      });
      setTotalPages(logsData.totalPages || 1);
    } catch (error) {
      console.error('Error loading send details:', error);
      toast({
        title: t('sendmirein.common.error'),
        description: t('sendmirein.history.detail.loadError'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Auto-refresh while sending
  useEffect(() => {
    if (!autoRefresh) return;
    if (send && send.status === 'SENDING') {
      const interval = setInterval(() => {
        loadData();
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [send?.status, autoRefresh]);

  // ETA helper
  function computeEta() {
    const done = stats.success + stats.errors;
    const total = send?.recipientCount || stats.total || 0;
    const remaining = Math.max(0, total - done);
    const perEmailMs = 200; // match backend
    const perBatchMs = 2000;
    const batchSize = 10;
    const fullBatches = Math.floor(remaining / batchSize);
    const leftover = remaining % batchSize;
    const estimateMs = fullBatches * (perBatchMs + batchSize * perEmailMs) + leftover * perEmailMs;
    const minutes = Math.floor(estimateMs / 60000);
    const seconds = Math.floor((estimateMs % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  }

  const filteredLogs = logs.filter(log => {
    if (filter === 'SUCCESS') return log.status === 'success';
    if (filter === 'ERROR') return log.status === 'error';
    return true;
  });

  // Keep logs view focused on newest entries while sending
  useEffect(() => {
    if (send?.status === 'SENDING' && logsContainerRef.current) {
      // Newest logs are at the top; ensure we scroll to top
      logsContainerRef.current.scrollTop = 0;
    }
  }, [send?.status, logs]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const locale = i18n?.language || 'de-DE';
    return new Date(dateString).toLocaleString(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-muted">
        <DashboardHeader />
        <div className="p-6">
          <div className="text-center py-12">{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  if (!send) {
    return (
      <div className="min-h-screen bg-muted">
        <DashboardHeader />
        <div className="p-6">
          <div className="text-center py-12 text-muted-foreground">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p>{t('sendmirein.history.detail.notFound')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted">
      <DashboardHeader />
        <div className="p-6 max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/sendmirein/history')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('sendmirein.common.back')}
          </Button>
          <h1 className="text-2xl font-bold">{t('sendmirein.history.detail.title')}</h1>
          {send?.status === 'SENDING' && (
            <div className="ml-auto flex items-center gap-3 text-sm">
              <span className="px-2 py-1 rounded bg-blue-100 text-blue-800">
                {t('sendmirein.history.detail.sendingLabel')} · {t('sendmirein.history.detail.progressLabel', { done: stats.success + stats.errors, total: (send?.recipientCount || stats.total) })} · {t('sendmirein.history.eta')} {computeEta()}
              </span>
              <Button variant="outline" size="sm" onClick={() => setAutoRefresh((v) => !v)}>
                {autoRefresh ? t('sendmirein.history.detail.autoRefreshOn') : t('sendmirein.history.detail.autoRefreshOff')}
              </Button>
            </div>
          )}
        </div>

        {/* Send Info Card */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-4">{send.subject}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">{t('sendmirein.history.detail.status')}:</span>
              <div className="font-medium mt-1">
                {send.status}
                {send.status === 'SENT' && (
                  <> ({t('sendmirein.history.successCount', { success: stats.success, total: (send.recipientCount || stats.total) })})</>
                )}
              </div>
            </div>
            <div>
              <span className="text-muted-foreground">{t('sendmirein.history.detail.recipients')}:</span>
              <div className="font-medium mt-1">{send.recipientCount || 0}</div>
            </div>
            <div>
              <span className="text-muted-foreground">{t('sendmirein.history.detail.sent')}:</span>
              <div className="font-medium mt-1">{formatDate(send.sentAt)}</div>
            </div>
          </div>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-6">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-blue-500" />
              <div>
                <div className="text-2xl font-bold">{stats.total}</div>
                <div className="text-sm text-muted-foreground">{t('sendmirein.history.detail.total')}</div>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-green-500" />
              <div>
                <div className="text-2xl font-bold text-green-600">{stats.success}</div>
                <div className="text-sm text-muted-foreground">{t('sendmirein.history.detail.success')}</div>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-3">
              <XCircle className="w-8 h-8 text-red-500" />
              <div>
                <div className="text-2xl font-bold text-red-600">{stats.errors}</div>
                <div className="text-sm text-muted-foreground">{t('sendmirein.history.detail.errors')}</div>
              </div>
            </div>
          </Card>
        </div>

        {/* Progress Bar while sending */}
        {send?.status === 'SENDING' && (
          <Card className="p-6">
            {(() => {
              const done = stats.success + stats.errors;
              const total = send?.recipientCount || stats.total || 0;
              const pct = total > 0 ? Math.round((done / total) * 100) : 0;
              return (
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span>{t('sendmirein.history.detail.progress')}</span>
                    <span>{done}/{total} ({pct}%)</span>
                  </div>
                  <div className="w-full h-3 bg-muted rounded">
                    <div
                      className="h-3 bg-blue-600 rounded"
                      style={{ width: `${pct}%`, transition: 'width 300ms' }}
                      aria-valuenow={pct}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      role="progressbar"
                    />
                  </div>
                </div>
              );
            })()}
          </Card>
        )}

        {/* Filters */}
        <Card className="p-4">
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={() => setFilter('ALL')}
              variant={filter === 'ALL' ? 'default' : 'outline'}
              size="sm"
            >
              {t('sendmirein.history.detail.filters.all', { count: logs.length })}
            </Button>
            <Button
              onClick={() => setFilter('SUCCESS')}
              variant={filter === 'SUCCESS' ? 'default' : 'outline'}
              size="sm"
            >
              <CheckCircle className="w-4 h-4 mr-1" />
              {t('sendmirein.history.detail.filters.success', { count: stats.success })}
            </Button>
            <Button
              onClick={() => setFilter('ERROR')}
              variant={filter === 'ERROR' ? 'default' : 'outline'}
              size="sm"
            >
              <XCircle className="w-4 h-4 mr-1" />
              {t('sendmirein.history.detail.filters.error', { count: stats.errors })}
            </Button>
          </div>
        </Card>

        {/* Logs Table */}
        <Card className="p-6">
          <h3 className="font-semibold mb-4">{t('sendmirein.history.detail.logsTitle', { page, totalPages })}</h3>
          {filteredLogs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {t('sendmirein.history.detail.noLogs')}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto max-h-80 overflow-y-auto" ref={logsContainerRef}>
                <table className="w-full text-sm">
                  <thead className="border-b">
                    <tr className="text-left">
                      <th className="pb-3 pr-4">{t('sendmirein.history.detail.table.status')}</th>
                      <th className="pb-3 pr-4">{t('sendmirein.history.detail.table.emailMasked')}</th>
                      <th className="pb-3 pr-4">{t('sendmirein.history.detail.table.time')}</th>
                      <th className="pb-3">{t('sendmirein.history.detail.table.error')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredLogs.map((log) => (
                      <tr key={log.id} className="hover:bg-muted">
                        <td className="py-3 pr-4">
                          {log.status === 'success' ? (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-500" />
                          )}
                        </td>
                        <td className="py-3 pr-4 font-mono text-xs">
                          {log.emailAnonymized}
                        </td>
                        <td className="py-3 pr-4 text-muted-foreground">
                          {formatDate(log.sentAt)}
                        </td>
                        <td className="py-3 text-red-600 text-xs max-w-md truncate">
                          {log.errorMessage || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    {t('sendmirein.history.detail.pagination.showing', { count: filteredLogs.length, total: stats.total })}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      {t('sendmirein.history.detail.pagination.prev')}
                    </Button>
                    <div className="flex items-center px-3 text-sm">
                      {t('sendmirein.history.detail.pagination.pageXofY', { page, totalPages })}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      {t('sendmirein.history.detail.pagination.next')}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default SendmireinHistoryDetail;
